import { create } from "zustand";
import type { User } from "../types";
import { auth } from "../api/endpoints";

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loadFromStorage: () => Promise<void>;
  switchCompany: (companyId: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: localStorage.getItem("auth_token"),
  isAuthenticated: !!localStorage.getItem("auth_token"),
  isAuthLoading: !!localStorage.getItem("auth_token"),  // loading if token exists (needs /me validation)

  login: async (email: string, password: string) => {
    // Single API call: login returns token + user info together (issue 4.7)
    const loginResponse = await auth.login(email, password);
    const token = loginResponse.access_token;

    localStorage.setItem("auth_token", token);
    set({ token, isAuthenticated: true, isAuthLoading: false, user: loginResponse.user });
  },

  logout: () => {
    localStorage.removeItem("auth_token");
    set({ user: null, token: null, isAuthenticated: false, isAuthLoading: false });
  },

  loadFromStorage: async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      set({ user: null, token: null, isAuthenticated: false, isAuthLoading: false });
      return;
    }

    set({ isAuthLoading: true });
    try {
      const user = await auth.getMe();
      set({ user, token, isAuthenticated: true, isAuthLoading: false });
    } catch {
      localStorage.removeItem("auth_token");
      set({ user: null, token: null, isAuthenticated: false, isAuthLoading: false });
    }
  },

  switchCompany: async (companyId: string) => {
    const response = await auth.switchCompany(companyId);
    const token = response.access_token;
    localStorage.setItem("auth_token", token);
    set({ token, isAuthenticated: true, user: response.user });
  },
}));
